import { Injectable } from '@angular/core';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { BcmService } from '@modules/bcm/bcm.service';
import { Logger } from '@core/services/logger.service';
import { RealBaseStateService } from '@bcmServices/real-base-state.service';
import { Notification } from '@shared/models/notification';
import { NotificationsApiService } from '@bcmApiServices/notifications.api-service';
import { formatISO } from '@core/date.facade';
import { BcmSockets } from '@modules/bcm/bcm-sockets.service';
import { TranslationService } from '@core/translation/translation.service';

const log = new Logger('NotificationsFacade');

@Injectable({providedIn: 'root'})
export class NotificationsFacade extends NotificationsApiService {

    private subscription: Subscription;

    private readonly state = new RealBaseStateService<Notification>();

    get notifications$(): Observable<Notification[]> {
        // here we just pass the state without any projections
        // it may happen that it is necessary to combine two or more streams and expose to the components
        return this.state.list$;
    }

    get selectedNotification$(): Observable<Notification> {
        // here we just pass the state without any projections
        // it may happen that it is necessary to combine two or more streams and expose to the components
        return this.state.selected$;
    }

    get loading$(): Observable<boolean> {
        // here we just pass the state without any projections
        // it may happen that it is necessary to combine two or more streams and expose to the components
        return this.state.isUpdating$;
    }

    constructor(private _socketService: BcmSockets,
                httpClient: HttpClient,
                bcmService: BcmService) {
        super(httpClient, bcmService);

        this._socketService.selectedTenantSocket
            .pipe(filter(v => !!v))
            .subscribe(socket => {
                this.subscription?.unsubscribe();
                this.subscription = socket.fromEvent(`notifications`)
                    .subscribe((notification: Notification) => {
                        this.state.addListItem(notification);
                    });
            });
    }

    loadAll(params?: HttpParams): Observable<Notification[]> {
        this.state.startUpdating();
        return this.getAll(params)
            .pipe(
                tap(berths => {
                    this.state.setList(berths);
                    this.state.stopUpdating();
                }),
                switchMap(() => this.notifications$)
            );
    }

    loadOne(id: number): Observable<Notification> {
        this.state.startUpdating();
        return this.getOne(id)
            .pipe(
                tap(item => {
                    this.state.setSelected(item);
                    this.state.stopUpdating();
                }),
                switchMap(() => this.selectedNotification$)
            );
    }

    markAsRead(notification: Notification): Observable<any> {

        if (notification?.id) {

            // this.state.startUpdating();

            const updatedBerth = {
                ...notification,
                status: {readOn: formatISO(new Date())}
            } as Notification;

            this.state.replaceListItem(notification, updatedBerth); // optimistic change

            return this.updateRead(notification)
                .pipe(
                    // tap(() => this.state.stopUpdating()),
                    catchError((error: HttpErrorResponse) => {
                        this.state.replaceListItem(updatedBerth, notification); // revert
                        return throwError(error);
                    })
                );
        }

        return throwError(TranslationService.translate('common.checkYourEntries'));
    }

    markAsDeleted(notification: Notification): Observable<any> {

        if (notification?.id) {

            this.state.removeListItem(notification);

            return this.updateDelete(notification)
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        this.state.addListItem(notification);
                        return throwError(error);
                    })
                );
        }

        return throwError(TranslationService.translate('common.checkYourEntries'));
    }

    resetList(): void {
        this.state.setList([]);
    }
}
